import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import ApplicantForm from '../components/ApplicantForm';
import ApplicantList from '../components/ApplicantList';
import SearchFilters from '../components/SearchFilters';
import ImportModal from '../components/ImportModal';

const Applicants = () => {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingApplicant, setEditingApplicant] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [lastJobId, setLastJobId] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    position: '',
    page: 1,
    limit: 50
  });
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchApplicants();
  }, [filters]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'new' && hasPermission('applicant', 'create')) {
      setEditingApplicant(null);
      setShowForm(true);
    }
  }, [location.search, hasPermission]);

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      params.set('sortBy', 'lastActivityAt');
      params.set('sortOrder', 'DESC');

      const response = await axios.get(`/api/applicants?${params.toString()}`);
      setApplicants(response.data.applicants);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching applicants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingApplicant(null);
    setShowForm(true);
  };

  const handleEdit = (applicant) => {
    setEditingApplicant(applicant);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this applicant?')) return;

    try {
      await axios.delete(`/api/applicants/${id}`);
      fetchApplicants();
    } catch (error) {
      alert('Error deleting applicant: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingApplicant(null);
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'new') {
      params.delete('action');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ''
        },
        { replace: true }
      );
    }
    fetchApplicants();
  };

  const handleImportSuccess = () => {
    setShowImport(false);
    fetchApplicants();
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Applicants</h1>
          <div className="flex space-x-3">
            {hasPermission('applicant', 'import') && (
              <button
                onClick={() => setShowImport(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
              Bulk Import
              </button>
            )}
            {hasPermission('applicant', 'create') && (
              <button
                onClick={handleCreate}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Add Applicant
              </button>
            )}
          </div>
        </div>

        <SearchFilters filters={filters} onFilterChange={handleFilterChange} />

        {showForm && (
          <ApplicantForm
            applicant={editingApplicant}
            onClose={handleFormClose}
            defaultJobId={lastJobId}
            onJobChange={setLastJobId}
          />
        )}

        {showImport && (
          <ImportModal
            onClose={() => setShowImport(false)}
            onSuccess={handleImportSuccess}
          />
        )}

        <ApplicantList
          applicants={applicants}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </div>
    </Layout>
  );
};

export default Applicants;

